import React, { useState, useEffect } from "react";
import {
  Bell,
  CalendarDays,
  MessageSquareWarning,
  Wallet,
  Search,
  BellRing,
  ChevronRight,
  BookOpen,
  UserPlus,
  CreditCard,
  CircleCheck,
  MapPin,
  Users,
  ClipboardList,
  Home,
  FileText,
  Menu,
  HandHeart, Sun, CloudSun, CloudFog, CloudRain, CloudSnow, CloudLightning, Calendar, Sparkles
} from "lucide-react";

import styles from "./ResidentDashboard.module.css";
import { useNavigate } from "react-router-dom";
import api from "../../api/axios";

const ResidentDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState({ fullName: 'Resident' });
  const [events, setEvents] = useState([]);
  const [notices, setNotices] = useState([]);
  const [visitors, setVisitors] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [maintenanceStats, setMaintenanceStats] = useState({ totalDue: 0, paid: 0, pending: 0, overdue: 0 });
  const [loading, setLoading] = useState(true);
  const [weather, setWeather] = useState({ temp: 32, condition: 'Partly Cloudy', city: 'Patna, India', icon: <Sun size={18} /> });

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        // Using Indore coordinates as requested
        const lat = 22.7196;
        const lon = 75.8577;
        const city = 'Indore';
        const country = 'India';

        const weatherRes = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current_weather=true`);
        const weatherData = await weatherRes.json();
        
        const temp = Math.round(weatherData.current_weather.temperature);
        const code = weatherData.current_weather.weathercode;
        
        let condition = 'Clear';
        let icon = <Sun size={32} />;
        
        if (code === 0) { condition = 'Clear sky'; icon = <Sun size={18} />; }
        else if (code === 1 || code === 2 || code === 3) { condition = 'Partly cloudy'; icon = <CloudSun size={18} />; }
        else if (code === 45 || code === 48) { condition = 'Fog'; icon = <CloudFog size={18} />; }
        else if (code >= 51 && code <= 67) { condition = 'Rain'; icon = <CloudRain size={18} />; }
        else if (code >= 71 && code <= 82) { condition = 'Snow'; icon = <CloudSnow size={18} />; }
        else if (code >= 95) { condition = 'Thunderstorm'; icon = <CloudLightning size={18} />; }
        
        setWeather({ temp, condition, city: `${city}, ${country}`, icon });
      } catch (err) {
        console.error("Weather fetch failed", err);
      }
    };
    
    fetchWeather();
  }, []);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        // Fallback user info from localStorage first
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
        if (userInfo && userInfo.fullName) setUser(userInfo);

        const [
          profileRes, 
          eventsRes, 
          noticesRes, 
          visitorsRes, 
          complaintsRes, 
          familyRes,
          maintenanceRes
        ] = await Promise.all([
          api.get('/users/profile'),
          api.get('/community/events'),
          api.get('/community/notices'),
          api.get('/users/visitors'),
          api.get('/users/complaints'),
          api.get('/family'),
          api.get('/maintenance/my-bills')
        ]);

        if (profileRes.data) {
          setUser(profileRes.data);
          const localUser = JSON.parse(localStorage.getItem('userInfo') || '{}');
          if (localUser.profileImage !== profileRes.data.profileImage) {
            localStorage.setItem('userInfo', JSON.stringify({ ...localUser, profileImage: profileRes.data.profileImage }));
            window.dispatchEvent(new Event('profileUpdated'));
          }
        }
        
        // Format Events
        setEvents(eventsRes.data.slice(0, 3).map(e => ({
          _id: e._id,
          title: e.title,
          date: new Date(e.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
          time: e.time,
          location: e.location || 'Community Hall',
          icon: <Sparkles size={16} />,
          type: "blue"
        })));

        // Format Notices
        setNotices(noticesRes.data.slice(0, 3).map(n => ({
          _id: n._id,
          title: n.title,
          date: new Date(n.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
          type: "orange",
          meetingDateTime: n.meetingDateTime,
          meetingLocation: n.meetingLocation
        })));

        // Format Visitors (Only expected today or checked in)
        setVisitors(visitorsRes.data.slice(0, 3).map(v => ({
          _id: v._id,
          name: v.name,
          flat: user.flatNumber ? `${user.wing || ''}-${user.flatNumber}` : 'N/A',
          time: v.expectedTime,
          initials: v.name.substring(0, 2).toUpperCase(),
          type: "green"
        })));

        setComplaints(complaintsRes.data);
        setFamilyMembers(familyRes.data);
        
        // Format Maintenance Stats
        let paid = 0;
        let pending = 0;
        let overdue = 0;
        
        maintenanceRes.data.forEach(bill => {
          paid += bill.paidAmount || 0;
          if (bill.status === 'Overdue') {
            overdue += bill.remainingAmount || 0;
          } else {
            pending += bill.remainingAmount || 0;
          }
        });
        
        setMaintenanceStats({
          totalDue: pending + overdue,
          paid,
          pending,
          overdue
        });
        
        setLoading(false);
      } catch (error) {
        console.error("Failed to fetch dashboard data:", error);
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const firstName = user.fullName ? user.fullName.split(" ")[0] : "Resident";
  
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    if (hour < 21) return 'Good Evening';
    return 'Good Night';
  };

  const pendingComplaintsCount = complaints.filter(c => c.status !== 'Resolved' && c.status !== 'Closed').length;

  // Helper to get correct image URL
  const getImageUrl = (url) => {
    if (!url) return 'https://cdn-icons-png.flaticon.com/512/3135/3135715.png';
    if (url.startsWith('http')) return url;
    return `${import.meta.env.VITE_API_URL.replace(/\/api\/?$/, '')}${url}`;
  };

  /* ================= STATS ================= */

  const stats = [
    {
      title: "New Notices",
      value: notices.length < 10 ? `0${notices.length}` : notices.length,
      action: "View all notices",
      icon: Bell,
      type: "green",
      path: "/resident/notices",
    },
    {
      title: "Upcoming Events",
      value: events.length < 10 ? `0${events.length}` : events.length,
      action: "View all events",
      icon: CalendarDays,
      type: "blue",
      path: "/resident/events",
    },
    {
      title: "Pending Complaints",
      value: pendingComplaintsCount < 10 ? `0${pendingComplaintsCount}` : pendingComplaintsCount,
      action: "View complaints",
      icon: MessageSquareWarning,
      type: "orange",
      path: "/resident/complaints",
    },
    {
      title: "Due Maintenance",
      value: `₹${maintenanceStats.totalDue.toLocaleString('en-IN')}`,
      action: "View details",
      icon: Wallet,
      type: "purple",
      path: "/resident/maintenance",
    },
  ];

  /* ================= QUICK ACTIONS ================= */
  const quickActions = [
    { label: 'Raise Complaint', icon: MessageSquareWarning, path: '/resident/complaints', color: 'green' },
    { label: 'Book Amenity', icon: BookOpen, path: '/resident/amenities', color: 'blue' },
    { label: 'Invite Visitor', icon: UserPlus, path: '/resident/visitors', color: 'purple' },
    { label: 'My Documents', icon: FileText, path: '/resident/documents', color: 'orange' },
    { label: 'Make Payment', icon: Wallet, path: '/resident/maintenance', color: 'teal' },
  ];

  /* ================= SUMMARY ================= */
  const summaries = [
    { label: 'Family Members', value: familyMembers.length < 10 ? `0${familyMembers.length}` : familyMembers.length, icon: Users, path: '/resident/family' },
    { label: 'My Flat', value: user.flatNumber ? `${user.wing || ''}-${user.flatNumber}` : 'Not Set', icon: Home, path: '/resident/profile' },
    { label: 'Open Requests', value: pendingComplaintsCount < 10 ? `0${pendingComplaintsCount}` : pendingComplaintsCount, icon: ClipboardList, path: '/resident/complaints' },
  ];

  if (loading) {
    return <div className={styles.loadingState}>Loading Dashboard...</div>;
  }

  /* ================= RETURN ================= */

  return (
    <div className={styles.dashboard}>
      <main className={styles.main}>
        {/* =====================================================
            CONTENT
        ====================================================== */}

        <section className={styles.content}>
          {/* =================================================
              WELCOME BANNER
          ================================================== */}

          <div className={styles.welcomeBanner}>
            <div className={styles.bannerContent}>
              <div className={styles.welcomeProfileGroup}>
                <div className={styles.welcomeInfo}>
                  <h2 className={styles.welcomeHeading}>
                    {getGreeting()}, {firstName}! <HandHeart size={28} color="#f59e0b" className={styles.greetingIcon} />
                  </h2>
                  <p className={styles.welcomeSubtitle}>Here's what's happening in your community today.</p>
                </div>
              </div>
              <div className={styles.weatherInfo}>
                <span>{weather.icon} {weather.temp}°C • {weather.condition} • {weather.city}</span>
              </div>
            </div>
            <div className={styles.bannerIllustration}>
              <svg viewBox="0 0 600 120" preserveAspectRatio="none" className={styles.citySvg}>
                {/* Background soft clouds */}
                <path d="M50,80 Q70,60 90,80 Q110,60 130,80" stroke="#f0f7f2" strokeWidth="15" fill="none" strokeLinecap="round" />
                <path d="M300,70 Q320,50 350,70 Q380,50 400,70" stroke="#f0f7f2" strokeWidth="20" fill="none" strokeLinecap="round" />

                {/* Birds */}
                <path d="M430,30 Q435,25 440,30 Q445,25 450,30" stroke="#7ac494" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                <path d="M480,40 Q485,35 490,40 Q495,35 500,40" stroke="#7ac494" strokeWidth="1.5" fill="none" strokeLinecap="round" />

                {/* Light Green Back Buildings */}
                <rect x="350" y="50" width="40" height="70" fill="#d2ead9" rx="2" />
                <rect x="360" y="30" width="20" height="20" fill="#d2ead9" rx="1" />
                <rect x="400" y="60" width="30" height="60" fill="#c4e3cc" rx="2" />
                <rect x="440" y="40" width="50" height="80" fill="#d2ead9" rx="2" />

                {/* Medium Green Buildings */}
                <rect x="250" y="45" width="45" height="75" fill="#a4d4b3" rx="3" />
                <rect x="310" y="65" width="35" height="55" fill="#a4d4b3" rx="2" />
                <polygon points="460,80 480,30 500,80" fill="#a4d4b3" />
                <rect x="460" y="80" width="40" height="40" fill="#a4d4b3" />
                <rect x="520" y="55" width="35" height="65" fill="#91ccaa" rx="2" />

                {/* Dark Green Front Buildings */}
                <rect x="270" y="70" width="25" height="50" fill="#6bc28c" rx="1" />
                <rect x="375" y="75" width="35" height="45" fill="#58b57b" rx="2" />
                <rect x="490" y="65" width="25" height="55" fill="#46a96b" rx="1" />

                {/* Ground */}
                <path d="M0,110 Q150,90 300,110 T600,110 L600,120 L0,120 Z" fill="#b0e0c1" />
                <path d="M0,115 Q150,105 300,115 T600,115 L600,120 L0,120 Z" fill="#69c78d" />

                {/* Trees */}
                <circle cx="280" cy="95" r="10" fill="#267845" />
                <circle cx="340" cy="85" r="15" fill="#1b6035" />
                <rect x="338" y="95" width="4" height="25" fill="#4a3b2c" />
                <circle cx="420" cy="90" r="12" fill="#2d8c52" />
                <circle cx="430" cy="95" r="8" fill="#267845" />
                <rect x="419" y="100" width="3" height="20" fill="#4a3b2c" />
                <circle cx="510" cy="100" r="8" fill="#1b6035" />
              </svg>
            </div>
          </div>

          {/* =================================================
              STAT CARDS
          ================================================== */}

          <div className={styles.statsGrid}>
            {stats.map((stat) => {
              const Icon = stat.icon;

              return (
                <div className={styles.statCard} key={stat.title}>
                  <div className={styles.statContent}>
                    <div className={`${styles.statIcon} ${styles[stat.type]}`}>
                      <Icon size={24} strokeWidth={1.5} />
                    </div>
                    <div className={styles.statInfo}>
                      <span className={styles.statTitle}>{stat.title}</span>
                      <strong className={styles.statValue}>{stat.value}</strong>
                    </div>
                  </div>
                  <button className={`${styles.statAction} ${styles[stat.type]}`} onClick={() => navigate(stat.path)}>
                    {stat.action} &rarr;
                  </button>
                </div>
              );
            })}
          </div>

          {/* =================================================
              MAIN GRID
          ================================================== */}

          <div className={styles.mainGrid}>
            {/* ================= EVENTS ================= */}

            <section className={styles.card}>
              <div className={styles.cardHeader}>
                <h2>Upcoming Events</h2>
                <button className={styles.viewLink} onClick={() => navigate("/resident/events")}>
                  View All
                </button>
              </div>

              <div className={styles.eventList}>
                {events.length === 0 && <p className={styles.emptyText}>No upcoming events</p>}
                {events.map((event) => (
                  <div className={styles.eventItem} key={event._id || event.title}>
                    <div
                      className={`${styles.eventIcon} ${styles[event.type]}`}
                    >
                      {event.icon}
                    </div>

                    <div className={styles.eventInfo}>
                      <h3>{event.title}</h3>
                      <div className={styles.eventMeta}>
                        <span>{event.date}</span>
                        <span>•</span>
                        <span>{event.time}</span>
                        <span className={styles.location}>
                          <MapPin size={12} className={styles.locationIcon} />
                          {event.location}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* ================= NOTICES ================= */}

            <section className={styles.card}>
              <div className={styles.cardHeader}>
                <h2>Recent Notices</h2>
                <button className={styles.viewLink} onClick={() => navigate("/resident/notices")}>
                  View All
                </button>
              </div>

              <div className={styles.noticeList}>
                {notices.length === 0 && <p className={styles.emptyText}>No new notices</p>}
                {notices.map((notice) => (
                  <div className={styles.noticeItem} key={notice._id || notice.title}>
                    <div
                      className={`${styles.noticeIcon} ${styles[notice.type]}`}
                    >
                      <Bell size={18} strokeWidth={1.5} />
                    </div>
                    <div className={styles.noticeInfo}>
                      <h3>{notice.title}</h3>
                      <p>{notice.date}</p>
                      {(notice.meetingDateTime || notice.meetingLocation) && (
                        <div className={styles.meetingDetails}>
                          {notice.meetingDateTime && <span><Calendar size={14} /> {new Date(notice.meetingDateTime).toLocaleString()}</span>}
                          {notice.meetingLocation && <span><MapPin size={14} /> {notice.meetingLocation}</span>}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* ================= MAINTENANCE ================= */}

            <section className={`${styles.card} ${styles.maintenanceCard}`}>
              <div className={styles.cardHeader}>
                <h2 className={styles.whiteText}>Maintenance Status</h2>
                <span className={styles.statusMonth}>May 2025</span>
              </div>

              <div className={styles.maintenanceContent}>
                {/* Donut */}
                <div className={styles.donutContainer}>
                  <div className={styles.donut}>
                    <div className={styles.donutInner}>
                      <strong>₹{maintenanceStats.totalDue.toLocaleString('en-IN')}</strong>
                      <span>Total Due</span>
                    </div>
                  </div>
                </div>

                {/* Details */}
                <div className={styles.paymentDetails}>
                  <div className={styles.paymentRow}>
                    <span className={styles.whiteTextLight}>
                      <i className={styles.paid}></i> Paid
                    </span>
                    <strong className={styles.whiteText}>₹{maintenanceStats.paid.toLocaleString('en-IN')}</strong>
                  </div>
                  <div className={styles.paymentRow}>
                    <span className={styles.whiteTextLight}>
                      <i className={styles.pending}></i> Pending
                    </span>
                    <strong className={styles.whiteText}>₹{maintenanceStats.pending.toLocaleString('en-IN')}</strong>
                  </div>
                  <div className={styles.paymentRow}>
                    <span className={styles.whiteTextLight}>
                      <i className={styles.overdue}></i> Overdue
                    </span>
                    <strong className={styles.whiteText}>₹{maintenanceStats.overdue.toLocaleString('en-IN')}</strong>
                  </div>
                </div>
              </div>

              <button className={styles.payButton} onClick={() => navigate("/resident/maintenance")}>
                <CreditCard size={15} /> Pay Now
              </button>
            </section>
          </div>

          {/* =================================================
              BOTTOM GRID
          ================================================== */}

          <div className={styles.bottomGrid}>
            {/* ================= QUICK ACTIONS ================= */}

            <section className={styles.card}>
              <div className={styles.cardHeader}>
                <h2>Quick Actions</h2>
              </div>
              <div className={styles.quickActions}>
                {quickActions.map((action, idx) => {
                  const Icon = action.icon;
                  return (
                    <button key={idx} className={styles.quickAction} onClick={() => navigate(action.path)}>
                      <span className={`${styles.qaIcon} ${styles[action.color]}`}>
                        <Icon size={22} strokeWidth={1.5} />
                      </span>
                      <small>{action.label}</small>
                    </button>
                  );
                })}
              </div>
            </section>

            {/* ================= VISITORS ================= */}

            <section className={styles.card}>
              <div className={styles.cardHeader}>
                <h2>My Visitors (Today)</h2>
                <button className={styles.viewLink} onClick={() => navigate("/resident/visitors")}>
                  View All
                </button>
              </div>

              <div className={styles.visitorList}>
                {visitors.length === 0 && <p className={styles.emptyText}>No visitors today</p>}
                {visitors.map((visitor, idx) => (
                  <div className={styles.visitorItem} key={visitor._id || idx}>
                    <div className={`${styles.visitorAvatar} ${styles[visitor.type]}`}>
                      {visitor.initials}
                    </div>
                    <div className={styles.visitorInfo}>
                      <h3>{visitor.name}</h3>
                      <span>{visitor.flat}</span>
                    </div>
                    <span className={styles.visitorTime}>{visitor.time}</span>
                    <span className={styles.inBadge}>
                      <CircleCheck size={12} />
                      In
                    </span>
                  </div>
                ))}
              </div>
            </section>
          </div>

          {/* =================================================
              SMALL COMMUNITY SUMMARY
          ================================================== */}

          <div className={styles.summaryGrid}>
            {summaries.map((sum, idx) => {
              const Icon = sum.icon;
              return (
                <div className={styles.summaryCard} key={idx}>
                  <div className={styles.summaryLeft}>
                    <div className={`${styles.summaryIcon} ${styles.green}`}>
                      <Icon size={20} strokeWidth={1.5} />
                    </div>
                    <div className={styles.summaryInfo}>
                      <span>{sum.label}</span>
                      <strong>{sum.value}</strong>
                    </div>
                  </div>
                  <button className={styles.viewLink} onClick={() => navigate(sum.path)}>
                    View Details &rarr;
                  </button>
                </div>
              );
            })}
          </div>
        </section>
      </main>
    </div>
  );
};

export default ResidentDashboard;
